import React, { useState, useEffect } from 'react';
import { Lead } from '../../types/lead.types';
import { TimeSlot } from '../../types/schedule.types';  // Changed from appointment.types
import { collection, query, where, onSnapshot, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import LeadDetailModal from '../leads/LeadDetailModal';
import { formatSafeDate, formatTimeRange } from '../../utils/dateUtils';
import { Calendar, Clock, User } from 'lucide-react';
import { useProject } from '../../contexts/ProjectContext';
import { appointmentService } from '../../services/appointment.service';
import { getLead } from '../../services/lead.service';
import LoadingSpinner from '../shared/LoadingSpinner';

// Define a local Appointment interface since it might not exist in schedule.types
interface Appointment {
  id: string;
  leadId: string;
  projectId: string;
  userId: string;
  date: Timestamp;
  time: string;
  startTime?: string;
  endTime?: string;
  type: 'tour' | 'call' | 'meeting';
  status: 'scheduled' | 'completed' | 'cancelled' | 'no-show';
  notes?: string;
  location?: string;
  duration?: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

interface AppointmentsListProps {
  projectId?: string;
  leadId?: string;
  limit?: number;
  showActions?: boolean;
}

const AppointmentsList: React.FC<AppointmentsListProps> = ({
  projectId,
  leadId,
  limit,
  showActions = true
}) => {
  const { selectedProject } = useProject();
  const projectTimezone = selectedProject?.settings?.timezone || '';
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showLeadModal, setShowLeadModal] = useState(false);

  useEffect(() => {
    let q = collection(db, 'appointments');
    const constraints: any[] = [];

    if (projectId) {
      constraints.push(where('projectId', '==', projectId));
    }
    if (leadId) {
      constraints.push(where('leadId', '==', leadId));
    }

    constraints.push(orderBy('date', 'desc'));
    
    const unsubscribe = onSnapshot(
      query(q, ...constraints),
      (snapshot) => {
        const appointmentData = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          } as Appointment))
          .slice(0, limit || snapshot.docs.length);
        
        setAppointments(appointmentData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching appointments:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId, leadId, limit]);

  const handleViewLead = async (appointment: Appointment) => {
    try {
      const lead = await getLead(appointment.leadId);
      if (lead) {
        setSelectedLead(lead);
        setShowLeadModal(true);
      }
    } catch (error) {
      console.error('Error fetching lead:', error);
    }
  };

  const handleStatusUpdate = async (appointmentId: string, newStatus: string) => {
    try {
      // Type cast the status to match the expected type
      await appointmentService.updateAppointment(appointmentId, { 
        status: newStatus as 'scheduled' | 'completed' | 'cancelled' | 'no-show' 
      });
    } catch (error) {
      console.error('Error updating appointment:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'no-show':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <LoadingSpinner />
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No appointments scheduled
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {appointments.map((appointment) => (
          <div key={appointment.id} className="bg-white border rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-4 mb-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-1" />
                    {formatSafeDate(appointment.date, 'MMM d, yyyy')}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Clock className="w-4 h-4 mr-1" />
                    {appointment.startTime && appointment.endTime
                      ? formatTimeRange(appointment.startTime, appointment.endTime, projectTimezone)
                      : appointment.time}
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(appointment.status)}`}>
                    {appointment.status}
                  </span>
                </div>
                
                <h4 className="font-medium text-gray-900">{appointment.type}</h4>
                
                {appointment.notes && (
                  <p className="text-sm text-gray-600 mt-1">{appointment.notes}</p>
                )}
                
                <div className="flex items-center mt-2 text-sm text-gray-500">
                  <User className="w-4 h-4 mr-1" />
                  <button
                    onClick={() => handleViewLead(appointment)}
                    className="text-indigo-600 hover:text-indigo-800"
                  >
                    View Lead Details
                  </button>
                </div>
              </div>
              
              {showActions && appointment.status === 'scheduled' && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleStatusUpdate(appointment.id, 'completed')}
                    className="text-sm text-green-600 hover:text-green-800"
                  >
                    Complete
                  </button>
                  <button
                    onClick={() => handleStatusUpdate(appointment.id, 'cancelled')}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {selectedLead && (
        <LeadDetailModal
          isOpen={showLeadModal}
          onClose={() => {
            setShowLeadModal(false);
            setSelectedLead(null);
          }}
          lead={selectedLead}
          onUpdate={(updatedLead) => {
            if (updatedLead) {
              setSelectedLead(updatedLead);
            }
          }}
        />
      )}
    </>
  );
};

export default AppointmentsList;